import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  User, Play, Mail, Phone, Clock, ChevronRight, Send, CheckCircle,
  Briefcase, BookOpen, Video, MessageCircle, Crown, Calendar, Share2, X, CalendarCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import ReportButton from '../components/ReportButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_BG = 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1920&h=800&fit=crop';

const mediaUrl = (u) => {
  if (!u) return '';
  let x = u;
  if (x.startsWith('/uploads/')) x = `/api${x}`;
  if (x.startsWith('/')) x = `${API_URL}${x}`;
  return x;
};

const getYtEmbed = (url) => {
  const m = url?.match(/(?:v=|embed\/|youtu\.be\/)([\w-]+)/);
  return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0&modestbranding=1` : url;
};

export default function MemberProfile() {
  const { userId } = useParams();
  const { user: currentUser, token } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('videos');
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [bookingNotes, setBookingNotes] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);

  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  useEffect(() => { fetchProfile(); }, [userId]);

  // Apply saved SEO (from the AI SEO assistant) to the document head so
  // shared/crawled micro-site links carry the optimized title/description,
  // Open Graph tags and a JSON-LD Person block (GEO — quoted by AI search).
  useEffect(() => {
    const u = profileData?.user;
    if (!u) return undefined;
    const seo = u.seo || {};
    const title = seo.seo_title || (u.name ? `${u.name} | WeMasterminds` : 'WeMasterminds');
    document.title = title;
    const desc = seo.meta_description || u.microsite_tagline || '';
    const photo = mediaUrl(u.profile_photo);
    const ensure = (attr, key, content) => {
      if (!content) return;
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    ensure('name', 'description', desc);
    ensure('name', 'keywords', (seo.keywords || []).join(', '));
    ensure('property', 'og:title', title);
    ensure('property', 'og:description', desc);
    ensure('property', 'og:type', 'profile');
    ensure('property', 'og:url', window.location.href);
    ensure('property', 'og:image', photo);
    ensure('name', 'twitter:card', 'summary');
    let ld = document.getElementById('member-profile-jsonld');
    if (!ld) {
      ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.id = 'member-profile-jsonld';
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ProfilePage',
      mainEntity: {
        '@type': 'Person',
        name: u.name || undefined,
        description: seo.geo_summary || desc || undefined,
        image: photo || undefined,
        url: window.location.href,
        knowsAbout: (seo.keywords || []).length ? seo.keywords : undefined,
        memberOf: { '@type': 'Organization', name: 'WeMasterminds' },
      },
    });
    return () => { ld.remove(); };
  }, [profileData]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/members/${userId}/profile`);
      setProfileData(res.data);
      if (!res.data?.videos?.length && res.data?.services?.length) setActiveTab('services');
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleBookService = async () => {
    if (!currentUser) { toast.error('Please login to book a service'); return; }
    if (!selectedDate || !selectedTime) { toast.error('Please select a date and time'); return; }
    setBookingLoading(true);
    try {
      if (selectedService.price > 0) {
        const res = await axios.post(`${API_URL}/api/scheduler/book-and-pay`, null, {
          params: { provider_id: userId, service_id: selectedService.id, booking_date: selectedDate, start_time: selectedTime, client_notes: bookingNotes, origin_url: window.location.origin },
          headers: { Authorization: `Bearer ${token}` }
        });
        window.location.href = res.data.checkout_url;
      } else {
        await axios.post(`${API_URL}/api/scheduler/bookings`, {
          provider_id: userId, service_id: selectedService.id, booking_date: selectedDate, start_time: selectedTime, client_notes: bookingNotes
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Booking confirmed!');
        setSelectedService(null); setSelectedDate(''); setSelectedTime(''); setBookingNotes('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book service');
    } finally { setBookingLoading(false); }
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leads/capture`, { member_id: userId, ...leadForm });
      setLeadSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally { setLeadSubmitting(false); }
  };

  const shareProfile = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) { await navigator.share({ title: displayName, url }); return; }
    } catch { /* fall through */ }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Profile link copied');
    } catch { toast.error('Could not copy link'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <p className="text-gray-400">Member not found</p>
      </div>
    );
  }

  const { user: member, profile, services, courses, videos } = profileData;
  const displayName = profile?.display_name || member.name;
  const coverImage = member?.microsite_cover_url || HERO_BG;
  const tagline = member?.microsite_tagline || '';
  const accent = member?.microsite_accent_color || '#D4A24A';
  const introVideo = member?.microsite_intro_video_url || '';
  const tier = member.membership_tier || 'free';
  const memberSince = member.member_since
    ? new Date(member.member_since).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '';

  let photo = profile?.profile_photo_url || member?.profile_photo || '';
  photo = mediaUrl(photo);

  const tabs = [
    { id: 'videos', label: 'Videos', icon: Video, count: videos?.length || 0 },
    { id: 'services', label: 'Services', icon: Briefcase, count: services?.length || 0 },
    { id: 'courses', label: 'Courses', icon: BookOpen, count: courses?.length || 0 },
    { id: 'contact', label: 'Contact', icon: MessageCircle, count: null },
  ];

  const scrollToTabs = () => document.getElementById('microsite-tabs')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-brand-bg text-white">
      {/* ===== Minimal public navbar ===== */}
      <header className="border-b border-white/5 bg-brand-bg/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="font-playfair text-xl font-bold" data-testid="profile-nav-home">
            We<span className="text-brand-gold">Masterminds</span>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/directory" className="hidden sm:block text-gray-400 hover:text-white transition">Directory</Link>
            <Link to="/courses" className="hidden sm:block text-gray-400 hover:text-white transition">Learn</Link>
            {currentUser ? (
              <Link to="/dashboard" className="px-4 py-2 rounded-lg bg-brand-gold text-black font-semibold hover:brightness-110 transition">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="px-4 py-2 rounded-lg border border-white/15 text-white hover:bg-white/5 transition">Sign in</Link>
                <Link to="/register" className="px-4 py-2 rounded-lg bg-brand-gold text-black font-semibold hover:brightness-110 transition">Join</Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ===== Hero ===== */}
      <section className="relative">
        <div className="h-72 md:h-96 w-full overflow-hidden relative">
          <img src={coverImage} alt="" className="w-full h-full object-cover" data-testid="microsite-hero-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/70 to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto px-6 -mt-24 md:-mt-32 relative z-10">
          <div className="flex flex-col md:flex-row md:items-end gap-6">
            {photo ? (
              <img src={photo} alt={displayName} className="w-32 h-32 md:w-40 md:h-40 rounded-2xl ring-4 ring-brand-gold/50 shadow-2xl object-cover" data-testid="member-profile-photo" />
            ) : (
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl ring-4 shadow-2xl bg-brand-surface flex items-center justify-center" style={{ '--tw-ring-color': `${accent}80` }}>
                <User className="w-16 h-16 text-white/40" />
              </div>
            )}
            <div className="flex-1 pb-2">
              <div className="flex items-center flex-wrap gap-3 mb-2">
                <h1 className="font-playfair text-4xl md:text-5xl font-semibold text-white" data-testid="profile-name">
                  {displayName}
                </h1>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1 uppercase ${
                  tier === 'founder' ? 'bg-brand-gold/20 text-brand-gold' :
                  tier === 'growth' ? 'bg-brand-blue/20 text-blue-300' :
                  'bg-white/10 text-gray-300'
                }`}>
                  <Crown className="w-3 h-3" /> {tier}
                </span>
              </div>
              {tagline && (
                <p className="text-lg font-light" style={{ color: accent }} data-testid="microsite-tagline">{tagline}</p>
              )}
              {profile?.bio && (
                <p className="text-gray-300 max-w-2xl mt-1">{profile.bio}</p>
              )}
              <div className="flex items-center flex-wrap gap-4 mt-3 text-sm text-gray-400">
                {member.specialty && (
                  <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" /> {member.specialty}</span>
                )}
                {memberSince && (
                  <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Joined {memberSince}</span>
                )}
              </div>
            </div>
            <div className="flex gap-2 pb-2">
              {introVideo && (
                <button
                  onClick={() => setShowIntro(true)}
                  className="px-5 py-3 border font-semibold rounded-xl hover:bg-white/5 transition flex items-center gap-2"
                  style={{ borderColor: `${accent}80`, color: accent }}
                  data-testid="watch-intro-btn"
                >
                  <Play className="w-4 h-4 fill-current" /> Watch my intro
                </button>
              )}
              <button
                onClick={() => { setActiveTab('services'); scrollToTabs(); }}
                className="px-5 py-3 text-black font-semibold rounded-xl hover:brightness-110 transition flex items-center gap-2"
                style={{ backgroundColor: accent }}
                data-testid="hero-cta-btn"
              >
                <CalendarCheck className="w-4 h-4" /> Book a service
              </button>
              <button onClick={shareProfile} className="px-4 py-3 border border-white/15 rounded-xl hover:bg-white/5 transition" data-testid="share-profile-btn">
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Tabs ===== */}
      <nav id="microsite-tabs" className="max-w-7xl mx-auto px-6 mt-12 border-b border-white/10 sticky top-[57px] bg-brand-bg z-30">
        <div className="flex gap-6 text-sm overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 border-b-2 transition whitespace-nowrap ${
                activeTab === tab.id ? '' : 'border-transparent text-gray-400 hover:text-white'
              }`}
              style={activeTab === tab.id ? { color: accent, borderColor: accent } : undefined}
              data-testid={`nav-${tab.id}`}
            >
              {tab.label}
              {tab.count !== null && <span className="ml-1 text-xs text-gray-500">{tab.count}</span>}
            </button>
          ))}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8 min-h-[50vh]">
        {/* ===== Videos ===== */}
        {activeTab === 'videos' && (
          <section>
            <h2 className="font-playfair text-2xl mb-4">Featured Videos</h2>
            {videos?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {videos.map(video => (
                  <div
                    key={video.id}
                    onClick={() => setPlayingVideo(video)}
                    className="relative rounded-xl overflow-hidden group cursor-pointer bg-brand-surface border border-white/5"
                    data-testid={`video-thumb-${video.id}`}
                  >
                    {video.thumbnail_url ? (
                      <img src={mediaUrl(video.thumbnail_url)} alt={video.title} className="w-full aspect-video object-cover" />
                    ) : (
                      <div className="w-full aspect-video bg-gradient-to-br from-brand-gold/10 to-brand-blue/10 flex items-center justify-center">
                        <Video className="w-10 h-10 text-white/20" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/40" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-all">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center shadow-xl" style={{ backgroundColor: accent }}>
                        <Play className="w-7 h-7 text-black fill-current ml-0.5" />
                      </div>
                    </div>
                    <div className="absolute bottom-3 left-3 right-3">
                      <p className="font-medium text-white">{video.title}</p>
                      {video.description && <p className="text-xs text-gray-300 line-clamp-1">{video.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-brand-surface border border-white/5 p-10 rounded-2xl text-center">
                <Video className="w-12 h-12 text-white/10 mx-auto mb-3" />
                <p className="text-gray-500">No videos published yet.</p>
              </div>
            )}
          </section>
        )}

        {/* ===== Services ===== */}
        {activeTab === 'services' && (
          <section>
            <h2 className="font-playfair text-2xl mb-4">Bookable Services</h2>
            {services?.length > 0 ? (
              <div className="space-y-3">
                {services.map(service => (
                  <div
                    key={service.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-xl bg-brand-surface border border-white/5 hover:border-brand-gold/30 transition"
                    data-testid={`service-${service.id}`}
                  >
                    <div>
                      <p className="text-lg font-medium text-white">{service.name}</p>
                      <p className="text-sm text-gray-400">{service.description}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                        <span className="flex items-center gap-1"><Clock size={12} /> {service.duration} min</span>
                        <span className="px-2 py-0.5 bg-white/5 rounded">{service.category}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-2xl font-playfair" style={{ color: accent }}>
                        {service.price > 0 ? `$${service.price}` : 'Free'}
                      </p>
                      <button
                        onClick={() => setSelectedService(service)}
                        className="mt-1 px-4 py-2 text-black text-xs font-semibold rounded-lg hover:brightness-110 transition"
                        style={{ backgroundColor: accent }}
                        data-testid={`book-now-${service.id}`}
                      >
                        Book now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-brand-surface border border-white/5 p-10 rounded-2xl text-center">
                <Briefcase className="w-12 h-12 text-white/10 mx-auto mb-3" />
                <p className="text-gray-500">No services available yet.</p>
              </div>
            )}
          </section>
        )}

        {/* ===== Courses ===== */}
        {activeTab === 'courses' && (
          <section>
            <h2 className="font-playfair text-2xl mb-4">Courses & Training</h2>
            {courses?.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {courses.map(course => (
                  <div key={course.id} className="bg-brand-surface border border-white/5 rounded-xl overflow-hidden hover:border-brand-gold/30 transition group">
                    {course.thumbnail_url ? (
                      <img src={course.thumbnail_url} alt={course.title} className="w-full h-44 object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-44 bg-gradient-to-br from-brand-gold/10 to-brand-blue/10 flex items-center justify-center">
                        <BookOpen className="w-12 h-12 text-white/20" />
                      </div>
                    )}
                    <div className="p-5">
                      <span className="text-brand-gold text-xs uppercase tracking-wider font-semibold">{course.category}</span>
                      <h3 className="text-lg font-medium text-white mt-1 mb-2">{course.title}</h3>
                      <p className="text-gray-400 text-sm mb-4 line-clamp-2">{course.description}</p>
                      <div className="flex justify-between items-center">
                        <span className="font-playfair text-xl" style={{ color: accent }}>{course.price > 0 ? `$${course.price}` : 'Free'}</span>
                        <Link to={`/courses/${course.id}`} className="text-white text-sm font-medium flex items-center gap-1 hover:gap-2 hover:text-brand-gold transition-all">
                          View <ChevronRight size={16} />
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-brand-surface border border-white/5 p-10 rounded-2xl text-center">
                <BookOpen className="w-12 h-12 text-white/10 mx-auto mb-3" />
                <p className="text-gray-500">No courses available yet.</p>
              </div>
            )}
          </section>
        )}

        {/* ===== Contact ===== */}
        {activeTab === 'contact' && (
          <section className="max-w-lg mx-auto">
            <h2 className="font-playfair text-2xl mb-6 text-center">Get in Touch</h2>

            {(member.contact_email || member.contact_phone || member.email || member.phone) && (
              <div className="bg-brand-surface border border-white/5 p-5 rounded-xl mb-6 space-y-3">
                {(member.contact_email || member.email) && (
                  <div className="flex items-center gap-3 text-gray-300">
                    <div className="w-8 h-8 bg-brand-gold/10 rounded-lg flex items-center justify-center">
                      <Mail size={16} className="text-brand-gold" />
                    </div>
                    <a href={`mailto:${member.contact_email || member.email}`} className="hover:text-brand-gold transition text-sm">
                      {member.contact_email || member.email}
                    </a>
                  </div>
                )}
                {(member.contact_phone || member.phone) && (
                  <div className="flex items-center gap-3 text-gray-300">
                    <div className="w-8 h-8 bg-brand-blue/10 rounded-lg flex items-center justify-center">
                      <Phone size={16} className="text-blue-300" />
                    </div>
                    <a href={`tel:${member.contact_phone || member.phone}`} className="hover:text-brand-gold transition text-sm">
                      {member.contact_phone || member.phone}
                    </a>
                  </div>
                )}
              </div>
            )}

            {leadSubmitted ? (
              <div className="bg-brand-surface border border-white/5 p-8 rounded-xl text-center">
                <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-xl font-medium text-white mb-2">Thank You!</h3>
                <p className="text-gray-400">Your message has been sent. {displayName} will be in touch soon.</p>
              </div>
            ) : (
              <form onSubmit={handleLeadSubmit} className="bg-brand-surface border border-white/5 p-6 rounded-xl space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Your Name *</label>
                  <Input value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} required className="bg-black/30 border-white/10 text-white" data-testid="lead-name" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Email *</label>
                  <Input type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} required className="bg-black/30 border-white/10 text-white" data-testid="lead-email" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Phone</label>
                  <Input type="tel" value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} className="bg-black/30 border-white/10 text-white" data-testid="lead-phone" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Message</label>
                  <Textarea value={leadForm.message} onChange={(e) => setLeadForm({ ...leadForm, message: e.target.value })} placeholder="How can I help you?" rows={4} className="bg-black/30 border-white/10 text-white" data-testid="lead-message" />
                </div>
                <Button type="submit" disabled={leadSubmitting} className="w-full text-black hover:brightness-110 font-semibold rounded-xl py-5" style={{ backgroundColor: accent }} data-testid="lead-submit-btn">
                  {leadSubmitting ? 'Sending...' : 'Send Message'}
                  <Send className="ml-2" size={18} />
                </Button>
              </form>
            )}
          </section>
        )}
      </main>

      {/* ===== Intro video modal ===== */}
      {showIntro && introVideo && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowIntro(false)} data-testid="intro-video-modal">
          <div className="w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-playfair text-xl text-white">Meet {displayName}</p>
              <div className="flex items-center gap-2">
                <ReportButton videoType="intro" videoId={member.id} variant="pill" />
                <button onClick={() => setShowIntro(false)} className="p-2 text-gray-400 hover:text-white" data-testid="intro-modal-close">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <video src={mediaUrl(introVideo)} controls autoPlay playsInline className="w-full aspect-video bg-black rounded-2xl border border-white/10" />
          </div>
        </div>
      )}

      {/* ===== Video player modal ===== */}
      {playingVideo && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPlayingVideo(null)} data-testid="video-player-modal">
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-playfair text-xl text-white">{playingVideo.title}</p>
              <div className="flex items-center gap-2">
                <ReportButton
                  videoType={playingVideo.video_type === 'youtube' ? 'youtube' : 'library'}
                  videoId={playingVideo.id}
                  variant="pill"
                />
                <button onClick={() => setPlayingVideo(null)} className="p-2 text-gray-400 hover:text-white" data-testid="video-modal-close">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            {playingVideo.video_type === 'youtube' ? (
              <iframe
                src={getYtEmbed(playingVideo.youtube_url)}
                className="w-full aspect-video rounded-2xl border border-white/10"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                title={playingVideo.title}
              />
            ) : (
              <video
                src={`${API_URL}/api/members/${userId}/videos/${playingVideo.id}/stream`}
                controls
                autoPlay
                playsInline
                className="w-full aspect-video bg-black rounded-2xl border border-white/10"
              />
            )}
          </div>
        </div>
      )}

      {/* ===== Booking modal ===== */}
      {selectedService && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelectedService(null)} data-testid="booking-modal">
          <div className="w-full max-w-md bg-brand-surface border border-white/10 rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-playfair text-xl text-white">Book: {selectedService.name}</h3>
              <button onClick={() => setSelectedService(null)} className="p-1 text-gray-400 hover:text-white" data-testid="booking-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Date</label>
                <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="bg-black/30 border-white/10 text-white" data-testid="booking-date" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Time</label>
                <Input type="time" value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)} className="bg-black/30 border-white/10 text-white" data-testid="booking-time" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Notes (optional)</label>
                <Textarea value={bookingNotes} onChange={(e) => setBookingNotes(e.target.value)} placeholder="Any additional details..." className="bg-black/30 border-white/10 text-white" data-testid="booking-notes" />
              </div>
              <div className="pt-4 border-t border-white/10">
                <div className="flex justify-between mb-4">
                  <span className="text-gray-400">Total</span>
                  <span className="text-2xl font-playfair" style={{ color: accent }}>
                    {selectedService.price > 0 ? `$${selectedService.price}` : 'Free'}
                  </span>
                </div>
                <Button onClick={handleBookService} disabled={bookingLoading || !selectedDate || !selectedTime} className="w-full text-black hover:brightness-110 font-semibold rounded-xl py-5" style={{ backgroundColor: accent }} data-testid="book-service-btn">
                  {bookingLoading ? 'Processing...' : selectedService.price > 0 ? 'Book & Pay' : 'Book Now'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Footer ===== */}
      <footer className="border-t border-white/5 py-8 mt-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">
            Powered by <Link to="/" className="text-brand-gold hover:underline font-medium">We Masterminds</Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
